import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import {
  fetchMerchantFormations,
  formationPrice,
  type PublicFormation,
} from '@/lib/publicFormations';

export default function FormationsPage() {
  const { tenant, isTenant, loading: tenantLoading } = useTenant();
  const [formations, setFormations] = useState<PublicFormation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  useEffect(() => {
    if (tenantLoading) return;
    if (!isTenant || !tenant) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const rows = await fetchMerchantFormations(tenant.id);
        if (!cancelled) setFormations(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Impossible de charger les formations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, tenant, tenantLoading]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    formations.forEach((f) => { if (f.categorie) set.add(f.categorie); });
    return Array.from(set);
  }, [formations]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return formations.filter((f) => {
      if (category !== 'all' && (f.categorie || '') !== category) return false;
      if (!q) return true;
      return `${f.titre} ${f.description || ''} ${f.souscategorie || ''}`.toLowerCase().includes(q);
    });
  }, [formations, query, category]);

  if (!isTenant || !tenant) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-graduation-cap-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Formations</h1>
        <p className="text-sm text-foreground-500 max-w-md">
          Retrouvez les formations directement sur le site du formateur.
        </p>
      </div>
    );
  }

  const freeCount = formations.filter((f) => formationPrice(f) <= 0).length;

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-100 via-background-50 to-primary-50"></div>
        <div className="relative w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold mb-4">
                <i className="ri-graduation-cap-line"></i>
                Catalogue de {tenant.nomcommerce || tenant.name}
              </span>
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                Nos <span className="text-primary-600">formations</span>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Choisissez une formation, créez votre compte en quelques secondes et retrouvez
                tous vos cours dans votre espace personnel. Les formations gratuites sont
                accessibles immédiatement, les formations payantes après règlement sécurisé.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="px-4 py-3 rounded-lg bg-background-50 border border-background-200/70">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground-400">Formations</p>
                <p className="text-xl font-bold font-heading text-foreground-950">{formations.length}</p>
              </div>
              <div className="px-4 py-3 rounded-lg bg-background-50 border border-background-200/70">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground-400">Gratuites</p>
                <p className="text-xl font-bold font-heading text-accent-600">{freeCount}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto -mt-6 relative z-10">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une formation…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>
          <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full overflow-x-auto max-w-full">
            <button
              onClick={() => setCategory('all')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                category === 'all' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              Toutes
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  category === c ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
            <p className="text-sm text-foreground-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-book-open-line text-2xl text-foreground-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-800 mb-1">Aucune formation</h3>
            <p className="text-sm text-foreground-500 max-w-md">
              Le formateur n&apos;a pas encore publié de formation.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {filtered.map((f) => {
              const price = formationPrice(f);
              return (
                <Link
                  key={f.id}
                  to={`/formation/${f.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-colors no-underline flex flex-col"
                >
                  <div className="relative aspect-[4/3] bg-background-100 overflow-hidden">
                    {f.image ? (
                      <img
                        src={f.image}
                        alt={f.titre}
                        title={`${f.titre} — formation en ligne`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-50">
                        <i className="ri-play-circle-line text-5xl text-primary-300"></i>
                      </div>
                    )}
                    <span className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      price > 0 ? 'bg-background-50/95 text-foreground-800' : 'bg-accent-500 text-background-50'
                    }`}>
                      {price > 0 ? `${price.toLocaleString('fr-FR')} ${currency}` : 'Gratuit'}
                    </span>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {f.titre}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mt-1.5">
                      {f.description || 'Programme détaillé disponible sur la fiche.'}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mt-3 text-[11px] text-foreground-500">
                      {f.categorie && <span className="px-2 py-0.5 rounded-full bg-background-100">{f.categorie}</span>}
                      {f.duree && <span className="flex items-center gap-1"><i className="ri-time-line"></i>{f.duree}</span>}
                    </div>
                    <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600">
                      Voir la formation <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}